import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Tag as TagIcon, Home, BookOpen, Heart, Mail, Link as LinkIcon, Check, Share2 } from "lucide-react";
import BottomNav from "@/components/site/BottomNav";
import { LOGO_URL } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso?.slice(0, 10) || ""; }
};

const decodeEntities = (s = "") =>
  s.replace(/&#8217;/g, "’").replace(/&#8216;/g, "‘")
    .replace(/&#038;/g, "&").replace(/&amp;/g, "&")
    .replace(/&#8211;/g, "–").replace(/&#8212;/g, "—")
    .replace(/&quot;/g, "\"").replace(/&#39;/g, "'");

export default function DevotionalDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null); setError(false);
    axios
      .get(`${API}/devotionals/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5 bg-cream">
        <div className="text-center" data-testid="devotional-404">
          <h1 className="font-display text-4xl text-navy-deep">Devotional not found</h1>
          <p className="mt-3 text-navy/60">The devotional you're looking for may not be published yet.</p>
          <Link to="/devotionals" className="mt-6 inline-block btn-primary px-6 py-3 rounded-full">
            Back to Devotionals
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-cream" data-testid="devotional-loading">
        <p className="text-navy/60">Loading devotional...</p>
      </div>
    );
  }

  return (
    <article data-testid="devotional-detail-page" className="bg-cream">
      {/* HERO */}
      <header className="page-hero" style={{ backgroundColor: "var(--navy-900)" }}>
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 70% 30%, rgba(217,184,100,0.45) 0%, transparent 50%)" }} />
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <Link
            to="/devotionals"
            data-testid="devotional-back"
            className="inline-flex items-center gap-2 text-cream/80 hover:text-gold-light text-sm mb-8"
          >
            <ArrowLeft size={16} /> All Devotionals
          </Link>
          <div className="flex items-center gap-3 text-xs text-cream/70 mb-4 flex-wrap">
            <Calendar size={14} className="text-gold-light" />
            <span data-testid="devotional-date">{fmtDate(post.published_at)}</span>
            {post.category_names?.length > 0 && (
              <>
                <span className="text-gold-light">·</span>
                {post.category_names.map((c) => (
                  <span key={c} className="px-2.5 py-0.5 rounded-full text-[0.65rem] uppercase tracking-wider bg-gold/20 text-gold-light border border-gold/30">
                    {decodeEntities(c)}
                  </span>
                ))}
              </>
            )}
          </div>
          <h1 data-testid="devotional-title" className="font-display text-4xl lg:text-6xl text-cream leading-tight">
            {decodeEntities(post.title)}
          </h1>
        </div>
      </header>

      {/* FEATURED IMAGE */}
      {post.featured_image_url && (
        <div className="max-w-4xl mx-auto px-5 lg:px-8 -mt-10 relative z-10">
          <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-gold/30">
            <img src={post.featured_image_url} alt={decodeEntities(post.title)} className="w-full h-auto" />
          </div>
        </div>
      )}

      {/* CONTENT */}
      <section className="py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          {post.excerpt && (
            <p className="text-xl lg:text-2xl text-navy-deep font-display leading-relaxed mb-10 pl-5 border-l-2 border-gold italic"
               data-testid="devotional-excerpt">
              {decodeEntities(post.excerpt)}
            </p>
          )}
          <div
            data-testid="devotional-content"
            className="wp-content prose-abide"
            dangerouslySetInnerHTML={{ __html: post.content_html || "" }}
          />

          <ShareButtons title={decodeEntities(post.title)} />

          {post.tag_names?.length > 0 && (
            <div className="mt-12 pt-6 border-t border-cream-200 flex flex-wrap items-center gap-2">
              <TagIcon size={14} className="text-gold" />
              {post.tag_names.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full text-xs bg-cream-100 text-navy-deep">
                  {decodeEntities(t)}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* AUTHOR / MINISTRY CARD */}
      <section className="py-12" style={{ backgroundColor: "var(--cream-100)" }}>
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl bg-white border border-cream-200 p-7 flex items-center gap-5">
            <img src={LOGO_URL} alt="Abide Daily emblem" className="w-20 h-20 object-contain shrink-0" />
            <div>
              <p className="font-display text-xl text-navy-deep">Abide Daily Ministry</p>
              <p className="text-sm text-navy/65 mt-1">
                Christ-centered daily devotionals to help you abide in Jesus.
              </p>
              <div className="mt-3 flex gap-3 flex-wrap">
                <Link to="/devotionals" className="text-gold font-semibold text-sm inline-flex items-center gap-1.5"><BookOpen size={14}/> More Devotionals</Link>
                <Link to="/prayer-requests" className="text-gold font-semibold text-sm inline-flex items-center gap-1.5"><Heart size={14}/> Submit Prayer</Link>
                <Link to="/" className="text-gold font-semibold text-sm inline-flex items-center gap-1.5"><Home size={14}/> Home</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BottomNav exclude={["/devotionals"]} />
    </article>
  );
}

function ShareButtons({ title }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `${title} — Abide Daily Ministry`;
  const enc = encodeURIComponent;
  const links = {
    whatsapp: `https://wa.me/?text=${enc(text + "\n" + url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}`,
    email: `mailto:?subject=${enc(text)}&body=${enc("I thought you might be encouraged by this devotional:\n\n" + url)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const btn = "inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-cream-200 hover:border-gold hover:text-navy-deep text-navy text-sm font-medium transition-colors";

  return (
    <div className="mt-12 pt-6 border-t border-cream-200" data-testid="share-buttons">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-3">
        <Share2 size={14} /> Share this devotional
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className={btn} aria-label="Share on WhatsApp">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true"><path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.4-1.4-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1-1.1 2.6 0 1.5 1.1 3 1.3 3.2.2.2 2.2 3.4 5.4 4.8.7.3 1.3.5 1.8.7.7.2 1.4.2 2 .1.6-.1 1.7-.7 2-1.4.3-.7.3-1.2.2-1.4-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.5c1.5.9 3.3 1.4 5.2 1.4 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.7 0-3.3-.5-4.7-1.3l-.3-.2-3.5 1.1 1.1-3.4-.2-.3c-.9-1.4-1.4-3-1.4-4.7C3 7 7 3 12 3s9 4 9 9-4 8.2-9 8.2z"/></svg>
          WhatsApp
        </a>
        <a href={links.facebook} target="_blank" rel="noopener noreferrer" data-testid="share-facebook" className={btn} aria-label="Share on Facebook">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true"><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z"/></svg>
          Facebook
        </a>
        <a href={links.email} data-testid="share-email" className={btn} aria-label="Share via Email">
          <Mail size={16} className="text-gold" /> Email
        </a>
        <button type="button" onClick={copyLink} data-testid="share-copy"
          className={`${btn} ${copied ? "border-green-300 text-green-800 bg-green-50" : ""}`}
          aria-label="Copy link to clipboard">
          {copied ? <><Check size={16} /> Copied</> : <><LinkIcon size={16} className="text-gold" /> Copy Link</>}
        </button>
      </div>
    </div>
  );
}
